import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';

export type TreatmentDatePoolEntry =
  paths['/v1/clinical-study/treatment-date-pool']['get']['responses'][200]['content']['application/json'][number];

export type TreatmentDatePoolPickerState =
  | { status: 'loading' }
  | { status: 'ready'; entries: TreatmentDatePoolEntry[] }
  | { status: 'forbidden' }
  | { status: 'error' };

/**
 * Reservation (Task 3.3) and the provisional-mode branch of the dual-mode reschedule (Task 4.2)
 * both need a Treatment Date Pool picker with remaining-capacity feedback.
 *
 * **SL-0045 Activity 9 changed the permission fact this hook was built around.** The list route
 * required `clinical-study:treatment-date-pool:manage` — a study-administrator-only permission the
 * Investigator and Assistant-Coordinator do not hold, which is why a `403` was routine for exactly
 * the roles that reserve, and why the consuming forms degraded to a raw pool-entry-ID input. That
 * route is now guarded `clinical-study:treatment-date-pool:read`, granted to both operational roles
 * and to the study administrator, so the routine `403` is gone and both raw-ID fallbacks were
 * deleted with it.
 *
 * `'forbidden'` REMAINS in the state union deliberately: a `403` is still reachable for any role
 * holding neither pool code, and it must surface as a refusal — the consuming forms route it to the
 * same presentation as `'error'`, never back to a hand-typed identifier. It stays a permission fact,
 * never auto-retried; call `refetch()` to retry after a genuinely transient `'error'`.
 */
export function useTreatmentDatePoolPicker(): TreatmentDatePoolPickerState & {
  refetch: () => void;
} {
  const [state, setState] = useState<TreatmentDatePoolPickerState>({ status: 'loading' });

  const load = () => {
    setState({ status: 'loading' });
    apiClient
      .GET('/v1/clinical-study/treatment-date-pool')
      .then(({ data, error, response }) => {
        if (response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        if (error !== undefined || !response.ok || data === undefined) {
          setState({ status: 'error' });
          return;
        }
        setState({ status: 'ready', entries: data });
      })
      .catch(() => setState({ status: 'error' }));
  };

  useEffect(load, []);

  return { ...state, refetch: load };
}
